import { CartAddEvent } from '@theme/events';

/**
 * <yegna-recipe-shop>
 *
 * Wraps the "Shop this recipe" rail. Per-item add buttons are native Horizon
 * product forms (they hook the cart drawer on their own). This element adds the
 * one-click "Add the whole recipe to cart" behaviour by batching every product's
 * variant into a single /cart/add request, then dispatching the theme's own
 * CartAddEvent so the cart drawer refreshes and auto-opens — the same path
 * Horizon's product-form uses for its internal batch add.
 */
class YegnaRecipeShop extends HTMLElement {
  connectedCallback() {
    this.addAllButton = this.querySelector('[data-yegna-add-all]');
    this.label = this.querySelector('[data-yegna-add-all-label]');
    this.defaultLabel = this.label?.textContent ?? 'Add the whole recipe to cart';
    this.addAllButton?.addEventListener('click', this.handleAddAll);
    // Quantity steppers (delegated, so it covers every card).
    this.addEventListener('click', this.handleStepper);
  }

  disconnectedCallback() {
    this.addAllButton?.removeEventListener('click', this.handleAddAll);
    this.removeEventListener('click', this.handleStepper);
  }

  /** Handles +/- clicks on any card's quantity stepper. */
  handleStepper = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const btn = target.closest('[data-qty-minus], [data-qty-plus]');
    if (!btn) return;
    const wrap = btn.closest('[data-yegna-qty]');
    const input = wrap && wrap.querySelector('[data-qty-input]');
    if (!input) return;
    const current = Math.max(1, parseInt(input.value, 10) || 1);
    input.value = String(btn.hasAttribute('data-qty-plus') ? current + 1 : Math.max(1, current - 1));
  };

  /** @returns {Array<{ id: number, quantity: number }>} */
  get items() {
    return Array.from(this.querySelectorAll('[data-yegna-variant-id]'))
      .map((card) => {
        const qtyInput = card.querySelector('[data-qty-input]');
        const quantity = Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);
        return { id: Number(card.getAttribute('data-yegna-variant-id')), quantity };
      })
      .filter((item) => Number.isFinite(item.id) && item.id > 0);
  }

  handleAddAll = async () => {
    const button = this.addAllButton;
    if (!button || button.hasAttribute('disabled')) return;

    const items = this.items;
    if (items.length === 0) return;

    button.setAttribute('disabled', '');
    if (this.label) this.label.textContent = 'Adding…';

    const cartAddUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add';
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.getAttribute('data-section-id'))
      .filter(Boolean)
      .join(',');

    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds }),
      });
      const data = await response.json();

      if (data.status) {
        // Surface a soft failure without breaking the page.
        if (this.label) this.label.textContent = 'Could not add, try again';
        console.error('Yegna add-all failed:', data.message || data.description || data);
        return;
      }

      let cart = null;
      try {
        cart = await (await fetch('/cart.js')).json();
      } catch (error) {
        console.error('Yegna add-all: cart fetch failed', error);
      }

      const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
      this.dispatchEvent(
        new CartAddEvent(cart ?? undefined, this.id || 'yegna-recipe-shop', {
          source: 'yegna-recipe',
          itemCount,
          sections: data.sections,
        })
      );

      if (this.label) this.label.textContent = 'Added to cart';
    } catch (error) {
      console.error('Yegna add-all error:', error);
      if (this.label) this.label.textContent = 'Could not add, try again';
    } finally {
      window.setTimeout(() => {
        button.removeAttribute('disabled');
        if (this.label) this.label.textContent = this.defaultLabel;
      }, 1600);
    }
  };
}

if (!customElements.get('yegna-recipe-shop')) {
  customElements.define('yegna-recipe-shop', YegnaRecipeShop);
}
